import { createTheme } from "@mui/material/styles";

import {
  ColorPrimaryMain,
  ColorPrimaryContrastText,
  ColorSecondaryMain,
  ColorSuccessMain,
  ColorSuccessContrastText,
  ColorErrorMain,
  ColorWarningMain,
  ColorBackgroundDefault,
  ColorBackgroundPaper,
  ColorPurpleLight,
  ColorPurpleMain,
  ColorPurpleDark,
  ColorPurpleContrastText,
  ColorRedLight,
  ColorRedMain,
  ColorRedDark,
  ColorRedContrastText,
  ColorBlueLight,
  ColorBlueMain,
  ColorBlueDark,
  ColorBlueContrastText,
} from "@beyondrealityapp/tokens/color";

import {
  TypographyFontFamilyBase,
  TypographyFontSizeH1,
  TypographyFontSizeH2,
  TypographyFontSizeH3,
  TypographyFontSizeH4,
  TypographyFontSizeH5,
  TypographyFontSizeH6,
  TypographyFontSizeSubtitle1,
  TypographyFontSizeSubtitle2,
  TypographyFontSizeBody1,
  TypographyFontSizeBody2,
  TypographyFontSizeButton,
  TypographyFontSizeCaption,
  TypographyFontSizeOverline,
  TypographyFontWeightH1,
  TypographyFontWeightH2,
  TypographyFontWeightH3,
  TypographyFontWeightH4,
  TypographyFontWeightH5,
  TypographyFontWeightH6,
  TypographyFontWeightSubtitle1,
  TypographyFontWeightSubtitle2,
  TypographyFontWeightBody1,
  TypographyFontWeightBody2,
  TypographyFontWeightButton,
  TypographyFontWeightCaption,
  TypographyFontWeightOverline,
  TypographyLineHeightH1,
  TypographyLineHeightH2,
  TypographyLineHeightH3,
  TypographyLineHeightH4,
  TypographyLineHeightH5,
  TypographyLineHeightH6,
  TypographyLineHeightSubtitle1,
  TypographyLineHeightSubtitle2,
  TypographyLineHeightBody1,
  TypographyLineHeightBody2,
  TypographyLineHeightButton,
  TypographyLineHeightCaption,
  TypographyLineHeightOverline,
  TypographyTextTransformButton,
  TypographyTextTransformOverline,
} from "@beyondrealityapp/tokens/typography";

import {
  SpacingBorderRadiusDefault,
} from "@beyondrealityapp/tokens/spacing";

import {
  ComponentButtonBorderRadius,
  ComponentCardMediaBorderRadius,
  ComponentCardMediaObjectFit,
  ComponentCardMediaMargin,
  ComponentDrawerBackgroundColor,
} from "@beyondrealityapp/tokens/components";

// Augment the palette to include custom colors
declare module "@mui/material/styles" {
  interface Palette {
    purple: Palette["primary"];
    red: Palette["primary"];
    blue: Palette["primary"];
  }

  interface PaletteOptions {
    purple?: PaletteOptions["primary"];
    red?: PaletteOptions["primary"];
    blue?: PaletteOptions["primary"];
  }
}

declare module "@mui/material/Button" {
  interface ButtonPropsColorOverrides {
    purple: true;
    red: true;
    blue: true;
  }
}

declare module "@mui/material/SvgIcon" {
  interface SvgIconColorOverrides {
    purple: true;
    red: true;
    blue: true;
  }
}

const theme = createTheme({
  palette: {
    primary: {
      main: ColorPrimaryMain,
      contrastText: ColorPrimaryContrastText,
    },
    secondary: {
      main: ColorSecondaryMain,
    },
    success: {
      main: ColorSuccessMain,
      contrastText: ColorSuccessContrastText,
    },
    error: {
      main: ColorErrorMain,
    },
    warning: {
      main: ColorWarningMain,
      contrastText: "#fff",
    },
    background: {
      default: ColorBackgroundDefault,
      paper: ColorBackgroundPaper,
    },
    purple: {
      light: ColorPurpleLight,
      main: ColorPurpleMain,
      dark: ColorPurpleDark,
      contrastText: ColorPurpleContrastText,
    },
    red: {
      light: ColorRedLight,
      main: ColorRedMain,
      dark: ColorRedDark,
      contrastText: ColorRedContrastText,
    },
    blue: {
      light: ColorBlueLight,
      main: ColorBlueMain,
      dark: ColorBlueDark,
      contrastText: ColorBlueContrastText,
    },
  },
  typography: {
    fontFamily: TypographyFontFamilyBase,
    h1: {
      fontSize: TypographyFontSizeH1,
      fontWeight: TypographyFontWeightH1,
      lineHeight: TypographyLineHeightH1,
    },
    h2: {
      fontSize: TypographyFontSizeH2,
      fontWeight: TypographyFontWeightH2,
      lineHeight: TypographyLineHeightH2,
    },
    h3: {
      fontSize: TypographyFontSizeH3,
      fontWeight: TypographyFontWeightH3,
      lineHeight: TypographyLineHeightH3,
    },
    h4: {
      fontSize: TypographyFontSizeH4,
      fontWeight: TypographyFontWeightH4,
      lineHeight: TypographyLineHeightH4,
    },
    h5: {
      fontSize: TypographyFontSizeH5,
      fontWeight: TypographyFontWeightH5,
      lineHeight: TypographyLineHeightH5,
    },
    h6: {
      fontSize: TypographyFontSizeH6,
      fontWeight: TypographyFontWeightH6,
      lineHeight: TypographyLineHeightH6,
    },
    subtitle1: {
      fontSize: TypographyFontSizeSubtitle1,
      fontWeight: TypographyFontWeightSubtitle1,
      lineHeight: TypographyLineHeightSubtitle1,
    },
    subtitle2: {
      fontSize: TypographyFontSizeSubtitle2,
      fontWeight: TypographyFontWeightSubtitle2,
      lineHeight: TypographyLineHeightSubtitle2,
    },
    body1: {
      fontSize: TypographyFontSizeBody1,
      fontWeight: TypographyFontWeightBody1,
      lineHeight: TypographyLineHeightBody1,
    },
    body2: {
      fontSize: TypographyFontSizeBody2,
      fontWeight: TypographyFontWeightBody2,
      lineHeight: TypographyLineHeightBody2,
    },
    button: {
      fontSize: TypographyFontSizeButton,
      fontWeight: TypographyFontWeightButton,
      lineHeight: TypographyLineHeightButton,
      textTransform: TypographyTextTransformButton as "uppercase",
    },
    caption: {
      fontSize: TypographyFontSizeCaption,
      fontWeight: TypographyFontWeightCaption,
      lineHeight: TypographyLineHeightCaption,
    },
    overline: {
      fontSize: TypographyFontSizeOverline,
      fontWeight: TypographyFontWeightOverline,
      lineHeight: TypographyLineHeightOverline,
      textTransform: TypographyTextTransformOverline as "uppercase",
    },
  },
  shape: {
    borderRadius: parseInt(SpacingBorderRadiusDefault),
  },
  components: {
    // #root is the scroll container (src/index.css); the body never scrolls.
    // MUI's scroll lock pads the body by innerWidth - clientWidth as if that
    // were a scrollbar, which in the iOS webview (see utils/layout/appWidth.ts)
    // squeezed the page behind every open dialog, menu and modal.
    MuiModal: {
      defaultProps: {
        disableScrollLock: true,
      },
    },
    // Menus and popovers pass their own disableScrollLock (default false) to
    // Modal, so they need the same default
    MuiPopover: {
      defaultProps: {
        disableScrollLock: true,
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          boxShadow: "none",
          borderRadius: ComponentButtonBorderRadius,
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          boxShadow: "none",
        },
      },
    },
    MuiAccordion: {
      styleOverrides: {
        root: {
          boxShadow: "none",
        },
      },
    },
    MuiCardMedia: {
      styleOverrides: {
        root: {
          borderRadius: ComponentCardMediaBorderRadius,
          objectFit: ComponentCardMediaObjectFit as "cover",
          margin: ComponentCardMediaMargin,
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          backgroundColor: ComponentDrawerBackgroundColor,
        },
      },
    },
  },
});

export default theme;
